import { useLayoutEffect, useState } from 'react';
import * as yup from 'yup';

import { OnboardingHouseholdCreateRequest } from '@ndvr/customer-api';

import { FormSheet } from '@/components/FormSheet';
import { PrimaryAdvisorSelector } from 'modules';
import { useAuthContext } from 'views/auth/Auth';
import { FormInput, FormSection, Select } from 'widgets';

import { useCreateOnboardingHousehold } from './onboarding-queries';

const createSchema = yup.object({
    householdName: yup.string().trim().required('Enter a household name'),
    firstName: yup.string().trim().required('Enter a first name'),
    lastName: yup.string().trim().required('Enter a last name'),
    email: yup.string().trim().email('Enter a valid email address'),
});

export const CreateOnboardingHouseholdSheet = ({
    onClose,
    onSuccess,
}: {
    onClose: () => void;
    onSuccess: (householdGUID: string) => void;
}) => {
    const { selectedTenants, user } = useAuthContext();
    const automaticTenantGUID = selectedTenants.length === 1 ? selectedTenants[0].tenantGUID : '';
    const scopeKey = selectedTenants.map(({ tenantGUID }) => tenantGUID).join('|');
    const [tenantGUID, setTenantGUID] = useState(automaticTenantGUID);
    const { mutate, isPending, isError } = useCreateOnboardingHousehold();

    useLayoutEffect(() => setTenantGUID(automaticTenantGUID), [automaticTenantGUID, scopeKey]);

    return (
        <FormSheet<OnboardingHouseholdCreateRequest>
            title="Create onboarding household"
            formSchema={createSchema}
            onClose={onClose}
            submitLabel="Create"
            submitDisabled={!tenantGUID}
            pending={isPending}
            onSubmit={(values) => {
                if (!tenantGUID) return;
                mutate(
                    {
                        tenantGUID,
                        request: {
                            householdName: values.householdName.trim(),
                            firstName: values.firstName.trim(),
                            middleName: values.middleName?.trim() || undefined,
                            lastName: values.lastName.trim(),
                            email: values.email?.trim() || undefined,
                            primaryAdvisorGUID: values.primaryAdvisorGUID || undefined,
                        },
                    },
                    { onSuccess: (status) => onSuccess(status.globalAccountGUID) },
                );
            }}
        >
            <p className="text-muted-foreground text-sm">Create the household record. No invitation will be sent.</p>
            {isError && (
                <p role="alert" className="text-destructive text-sm">
                    Could not create the household. Check the details and try again.
                </p>
            )}
            <fieldset disabled={isPending} className="contents">
                {selectedTenants.length !== 1 && (
                    <FormSection title="Client Base">
                        <Select
                            aria-label="Client Base"
                            value={tenantGUID}
                            onChange={(event) => setTenantGUID(event.target.value)}
                        >
                            <option value="">Select Client Base</option>
                            {selectedTenants.map((tenant) => (
                                <option key={tenant.tenantGUID} value={tenant.tenantGUID}>
                                    {tenant.name}
                                </option>
                            ))}
                        </Select>
                    </FormSection>
                )}
                <FormSection title="Household">
                    <FormInput name="householdName" label="Household name" placeholder="Household name" />
                </FormSection>
                <FormSection title="Primary contact">
                    <FormInput name="firstName" label="First name" placeholder="First name" />
                    <FormInput name="middleName" label="Middle name (optional)" placeholder="Middle name" />
                    <FormInput name="lastName" label="Last name" placeholder="Last name" />
                    <FormInput name="email" label="Email (optional)" placeholder="Email address" type="email" />
                </FormSection>
                <FormSection title="Advisor">
                    <PrimaryAdvisorSelector
                        tenantGUID={tenantGUID || undefined}
                        defaultAdvisorGUID={user?.personOrgGUID}
                    />
                </FormSection>
            </fieldset>
        </FormSheet>
    );
};
